import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios'; // npm i axios #see https://github.com/axios/axios

import { VERSION, API_URL, DEBUG_MODE } from "../config";
import { clientDate } from './tools';
import { INTERNAL_SERVER_ERROR } from './httpStatusCodes';


export class APIError extends Error {
    private _status: number | undefined;
    private _code: string | undefined;

    constructor(message: string, status: number | undefined, code: string | undefined) {
        super(message);

        this._status = status;
        this._code = code;
    }

    public get status(): number | undefined {
        return this._status;
    }

    public set status(status: number | undefined) {
        this._status = status;
    }

    public get code(): string | undefined {
        return this._code;
    }

    public set code(code: string | undefined) {
        this._code = code;
    }
}

async function apiCall<R, D, T extends { data?: R; exectime?: number; }>(
    method: "get" | "post" | "put" | "delete", url: string, data?: D, config?: AxiosRequestConfig<D>
) {
    const apiURL = `${API_URL}/${url}`;

    const params: [url: string, data?: D, config?: AxiosRequestConfig<D>] = [apiURL];

    if (data) params.push(data);

    const clientStack: string[] = [];
    const stackTrace = new Error().stack?.split("\n").map(s => s.split(/<?@/)[0]);
    if (stackTrace)
        for (const s of stackTrace) {
            clientStack.push(s);

            if (s.startsWith("./client")) break;
        }

    const urlGenerationFailure = ["undefined", "unknown", "[object Object]"].some(x => apiURL.includes(x));

    if (urlGenerationFailure) console.error("URL generation failure:", method, `${apiURL}`, clientStack);

    params.push(Object.assign({
        headers: {
            'adt-client-date': clientDate(),
            'adt-client-version': VERSION,
            'adt-client-stack': (DEBUG_MODE || urlGenerationFailure) ?
                `[${clientStack.map(s => `"${s}"`).join(", ")}]` :
                undefined
        }
    }, config ?? {}));

    if (DEBUG_MODE)
        console.info(`Request: ${method} ${apiURL}`, ...params);

    return await Axios[method]<T, AxiosResponse<T, D>, D>(...params)
        .then(response => {
            if (DEBUG_MODE)
                console.info(`${apiURL}: { status: ${response.status}, exec-time: ${response.data?.exectime ?? "unknown"} ms }`);

            return response.data;
        })
        .catch(err => {
            if (DEBUG_MODE) console.error(err);

            throw new APIError(err.response?.data ?? err.message ?? "Unknown API error",
                err.status ?? INTERNAL_SERVER_ERROR, err.code);
        });
}

export async function getCall<R, T = { data?: R; exectime?: number; }>(url: string, config?: AxiosRequestConfig<undefined>) {
    return await apiCall<R, undefined, T>("get", url, undefined, config);
}

export async function postCall<R, D, T = { data?: R; exectime?: number; }>(url: string, data: D, config?: AxiosRequestConfig<D>) {
    return await apiCall<R, D, T>("post", url, data, config);
}

export async function putCall<R, D, T = { data?: R; exectime?: number; }>(url: string, data: D, config?: AxiosRequestConfig<D>) {
    return await apiCall<R, D, T>("put", url, data, config);
}

export async function deleteCall<R, T = { data?: R; exectime?: number; }>(url: string, config?: AxiosRequestConfig<undefined>) {
    return await apiCall<R, undefined, T>("delete", url, undefined, config);
}

export async function getRequest(uri: string, headers = {}, opt = {}) {
    return await getCall<any>(uri, Object.assign({ headers }, opt));
}

export async function putRequest(uri: string, data: object, headers = {}, opt = {}) {
    return await putCall<any, any>(uri, data, Object.assign({ headers }, opt));
}

export async function postRequest(uri: string, data: object, headers = {}, opt = {}) {
    return await postCall<any, any>(uri, data, Object.assign({ headers }, opt));
}

export async function deleteRequest(uri: string, headers = {}, opt = {}) {
    return await deleteCall<any>(uri, Object.assign({ headers }, opt));
}

/**
 * Route: DELETE /api/cases/discard/:cas_uuid
 */
export async function httpDiscard(case_uuid: string) {
    return await deleteRequest(`cases/discard/${case_uuid}`);
}

/**
 * Route: DELETE /api/cases/restore/:cas_uuid
 */
export function httpRestore(case_uuid: string, success, error) {
    deleteRequest(`cases/restore/${case_uuid}`, success, error);
}
